import React, { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useTranslation } from "react-i18next"
import SearchSuggestionsSection from "./SearchSuggestionsSection"
import { cn } from "../../../../../lib"
import RecentSearchesV2 from "./RecentSearchesV2"
import CityInput from "./CityInput"

const SearchSuggestionsModal = ({
  ref,
  activeField,
  isOvercrowded,
  fetchTerminals,
  selectAirport,
  filteredSuggestions,
  onQueryChange,
  onClearTerminals,
  selectedDeparture,
  selectedArrival,
  initialQuery = "",
}) => {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const containerRef = useRef(null)
  const terminalsRef = useRef(null)
  const [query, setQuery] = useState(initialQuery)

  const isDeparture = activeField === "from"

  const terminals = isDeparture ? selectedDeparture : selectedArrival

  // фокус при открытии
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // скролл тегов — та же логика что в CityField
  const calculateOffset = () => {
    if (!containerRef.current || !terminalsRef.current) return

    const containerWidth = containerRef.current.offsetWidth
    const terminalsWidth = terminalsRef.current.scrollWidth
    const minInputWidth = containerWidth * 0.1
    const availableForTerminals = containerWidth - minInputWidth

    if (terminalsWidth > availableForTerminals) {
      terminalsRef.current.scrollLeft = terminalsWidth - availableForTerminals
    } else {
      terminalsRef.current.scrollLeft = 0
    }
  }

  useEffect(() => {
    window.addEventListener("resize", calculateOffset)
    return () => window.removeEventListener("resize", calculateOffset)
  }, [])

  useEffect(() => {
    if (!terminals?.length) {
      if (terminalsRef.current) terminalsRef.current.scrollLeft = 0
      return
    }
    const timeoutId = setTimeout(calculateOffset, 310)
    return () => clearTimeout(timeoutId)
  }, [terminals])

  const handleChange = (e) => {
    const value = e.target.value
    setQuery(value)
    onQueryChange?.(value)
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scaleY: 0 }}
      animate={{ opacity: 1, scaleY: 1 }}
      exit={{ opacity: 0, scaleY: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      style={{ transformOrigin: "top" }}
      className={cn(
        "absolute z-50 bg-white shadow-2xl overflow-x-hidden",
        "-top-[1px] w-full min-w-[400px] max-w-sm border border-gray-100",
        activeField === "from" ? "left-0" : "right-0",
        isDeparture ? "rounded-l-md" : "rounded-r-md"
      )}
    >
      {/* Шапка — теги + инпут, как в CityField */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-100 px-3 py-2">
        <div ref={containerRef} className="flex items-center w-full min-w-0 h-[37px]">
          <div
            ref={terminalsRef}
            className="overflow-hidden flex items-center min-w-0 max-w-[90%] flex-shrink-0"
            style={{ scrollBehavior: "smooth" }}
          >
            <div className="flex items-center gap-1 whitespace-nowrap">
              <AnimatePresence mode="popLayout" onExitComplete={calculateOffset}>
                {terminals?.map((item) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, x: -20, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 20, scale: 0.9 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <CityInput value={item.city} onClear={() => onClearTerminals(item.id)} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleChange}
            placeholder={
              activeField === "from" ? t("admin.city_of_departure") : t("admin.city_of_arrival")
            }
            className="flex-1 min-w-[10%] pl-2 text-sm outline-none"
          />
        </div>
      </div>

      {/* Контент */}
      <div className="p-2 max-h-80 overflow-y-auto">
        <RecentSearchesV2 fetchTerminals={fetchTerminals} />

        {filteredSuggestions.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-dark-gray mb-2">
              {t("admin.included_search")}
            </h3>
            {filteredSuggestions.map((section) => (
              <SearchSuggestionsSection
                key={section?.country_code}
                section={section}
                selectAirport={(id) => selectAirport(id, activeField)}
                isOvercrowded={isOvercrowded}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default SearchSuggestionsModal
